import { Space } from 'antd';
import { NoNgChartProps } from '../../../../../@types/kr/types';
import { useTheme } from '../../../../../providers/theme';
import { getColors } from '../../../../utils/getColors';
import { PointClickEventObject, SeriesOptionsType } from 'highcharts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import MeanValue from './meanValue';
import { Chart } from '../../../../chart/BaseChart';
import { ChartSpec } from '../../../../chart/chartSpec';

const KrChart = (props: NoNgChartProps) => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const [meanLineData, setMeanLineData] = useState<
    (number | null | undefined)[][]
  >([]);
  const [chartData, setChartData] = useState<number[][]>([]);
  const [hiddenChartData, setHiddenChartData] = useState<[number, number][]>([]);


  const getMeanData = () => {
    const data = props.sg.map(sw => [Number(sw.value), props.mean]);
    setMeanLineData(data);
  };

  useEffect(() => {
    if (props.values?.length && props.mean !== undefined) {
      getMeanData();
    }
  }, [props.values, props.mean]);

  // Separate visible and hidden points
  useEffect(() => {
    const visiblePoints: [number, number][] = [];
    const hiddenPoints: [number, number][] = [];

    props.sg.forEach((sgItem, index) => {
      const valueItem = props.values?.[index];
      const point: [number, number] = [sgItem.value, valueItem?.value ?? 0];

      if (sgItem.display && valueItem?.display) {
        visiblePoints.push(point);
      } else {
        hiddenPoints.push(point);
      }
    });

    setChartData(visiblePoints);
    setHiddenChartData(hiddenPoints);
  }, [props.values, props.sg]);

  const handlePointClick = useCallback((event: PointClickEventObject) => {
    const sg = event.point.x;
    const value = event.point.y;

    const updatedValues = props.values.map((item, index) => {
      const sgItem = props.sg[index];
      if (item.value === value && sgItem.value === sg) {
        return { ...item, display: !item.display };
      }
      return item;
    });

    const updatedSw = props.sg.map((item, index) => {
      const valueItem = props.values?.[index];
      if (item.value === sg && valueItem?.value === value) {
        return { ...item, display: !item.display };
      }
      return item;
    });

    props.setSg(prev => {
      const updatedState = { ...prev };
      if (props.type === 'No') {
        updatedState.no = updatedSw;
      } else if (props.type === 'Ng') {
        updatedState.ng = updatedSw;
      }
      return updatedState;
    });

    props.setValues(updatedValues);
  }, [props.values, props.sg, props.type, props.setSg, props.setValues]);

  const disabledColor = 'rgba(150, 150, 150, 0.3)';

  const spec: ChartSpec = useMemo(() => ({
    title: 'Corey Exponents',
    xAxis: { title: 'Water Saturation' },
    yAxis: { title: `Oil Corey Exponents (${props.type})`, type: 'linear' },
    series: [
      {
        name: `${props.type}'`,
        type: 'scatter',
        data: chartData,
        color: props.color
      },
      {
        name: `${props.type}' (disabled)`,
        type: 'scatter',
        data: hiddenChartData,
        marker: {
          symbol: 'circle',
          fillColor: 'transparent',
          lineColor: disabledColor,
          lineWidth: 1,
          radius: 3
        }
      },
      {
        name: `${props.type} Line`,
        type: 'line',
        data: meanLineData,
        marker: {
          enabled: false
        },
        color: props.color,
        lineWidth: 4
      }
    ] as SeriesOptionsType[],
    onPointClick: handlePointClick
  }), [chartData, hiddenChartData, meanLineData, props.type, props.color, handlePointClick]);

  return (
    <div style={{ marginLeft: '0.5rem' }}>
      <Space
        direction="vertical"
        style={{
          backgroundColor: 'transparent',
          border: '1px solid #434343',
          borderRadius: '16px',
          transition: 'width 0.5s',
          width: '100%'
        }}
      >
        <Space style={{ display: 'block' }}>
          <div style={{ padding: '0.75rem' }}>
            <Chart spec={spec} />
          </div>
        </Space>
      </Space>
      {props.type === 'No' && (
        <MeanValue
          mean={props.mean}
          savedMean={props.savedMean}
          setMean={props.setMean}
          setSavedMean={props.setSavedMean}
          type={props.type}
        />
      )}
      {props.type === 'Ng' && (
        <MeanValue
          mean={props.mean}
          savedMean={props.savedMean}
          setMean={props.setMean}
          setSavedMean={props.setSavedMean}
          type={props.type}
        />
      )}
    </div>
  );
};

export default KrChart;